/**
 * Milk trading (DM-3, Ronald on the decision sheet, 18 October 2026: "the map stands"): the contracts and the
 * offers with their prices, entered and checked here by milk trading and the milk office, read by management.
 *
 * On the Dairy OS map this page sits in the Milk Trading workspace, beside Sales and Sales order in
 * bookkeeping-automation; a workspace may span applications. Milk sales (/sales) shows the same orders in loads
 * and litres for the transport office, without a price.
 */
import { Explainer, PageHeader, Section, Select, SelectItem } from "@depicomnlbv/platform-ui";
import { useSearchParams } from "react-router-dom";

import { useBuyers, useContracts } from "../api";
import { useMilkFlowTimeline } from "../api/milkFlow";
import { MilkIntake } from "../components/MilkIntake";
import { londonToday, mondayOf } from "../time";
import { useTitle } from "../useTitle";
import { Contracts, NewContractForm } from "./MilkSales";
import { WeeksTable } from "./MilkFlow";
import { Offers } from "./SalesOffers";
import "./milkSales.css";

const ALL = "all";

export function TradingPage() {
  useTitle("Milk trading");
  const buyers = useBuyers();
  const contracts = useContracts();
  // The weeks to sell or to buy moved here from Milk Flow (SC-4, Ronald on the decision sheet, 19 October 2026).
  // From this week's Monday for two weeks, so both weeks are whole and get a figure.
  const timeline = useMilkFlowTimeline(mondayOf(londonToday()), 14);
  const weeks = timeline.data?.weeks ?? [];
  // Where the milk goes, from today (US-7 A, Ronald on the decision sheet, 21 October 2026): trading is kept apart
  // from the factory's silos, so the barrels are here and not on Milk Flow.
  const intake = timeline.data?.trade_days ?? [];
  const [search, setSearch] = useSearchParams();
  const names = buyers.data?.buyers.map((b) => b.name) ?? [];
  const asked = search.get("buyer");
  const buyer = asked && names.includes(asked) ? asked : null;
  return (
    <div className="page page--blocks">
      <PageHeader
        actions={
          names.length > 0 ? (
            <Select
              label="Show trading with"
              onSelectionChange={(key) => setSearch(key === ALL ? {} : { buyer: String(key) }, { replace: true })}
              selectedKey={buyer ?? ALL}
            >
              <SelectItem id={ALL}>All buyers</SelectItem>
              {names.map((n) => (
                <SelectItem id={n} key={n}>
                  {n}
                </SelectItem>
              ))}
            </Select>
          ) : null
        }
        title="Milk trading"
      />
      {intake.length > 0 ? (
        <Section flush title="Milk in, by destination">
          <MilkIntake days={intake} />
        </Section>
      ) : null}
      {weeks.length > 0 ? (
        <Section flush title="Weeks">
          <WeeksTable weeks={weeks} />
        </Section>
      ) : null}
      <Offers buyer={buyer} />
      <Section title="Contracts">
        <Contracts buyer={buyer} />
      </Section>
      {/* Only for who may enter a contract; management reads (DM-3). */}
      {contracts.data?.can_write ? <NewContractForm /> : null}
      <Explainer summary="How milk trading works">
        <dl className="explainer-list">
          <dt>Offers</dt>
          <dd>
            What we offer to sell before the buyer&rsquo;s confirmation, per week: proposed, offered, agreed with the
            buyer&rsquo;s price, then contracted when the buyer&rsquo;s own confirmation is linked.
          </dd>
          <dt>Contracts</dt>
          <dd>
            The buyer&rsquo;s confirmations with their price per litre, each checked once against its filed document
            by a person. Recorded by milk trading or the milk office; management reads them.
          </dd>
          <dt>Milk in, by destination</dt>
          <dd>
            Per day the milk our producers give: what goes to Gwalchmai as the factory ordered it (the batches Block
            days allows), what goes into each buyer&rsquo;s barrels, and what is still to trade. A barrel agreed or
            contracted is solid; one proposed, offered or tendered is dashed.
          </dd>
          <dt>Where the litres are</dt>
          <dd>
            The same orders in loads and litres, without a price, are on Milk sales for the transport office. What is
            invoiced and paid is on Sales in the bookkeeping application.
          </dd>
        </dl>
      </Explainer>
    </div>
  );
}
